import { makeStyles } from '@material-ui/styles';
import getAppData from '@connectors/newport/appData/getAppData';
import { useEffect, useState } from 'react';
import useUserStore, { Wishlist } from '@services/stores/UserStore';
import NavigationLayout from '@components/account/NavigationLayout';
import MyNewportImage from '@components/account/MyNewportImage';
import { WishlistProductId } from '@services/MyNewportService';
import { useTranslation } from '@tools/i18n';
import CallToActionButton from '@components/products/partials/CallToActionButton';
import { GetServerSidePropsContext } from 'next';
import theme from '@components/theme';
import Link from 'next/link';
import If from '@components/misc/If';

const useStyles = makeStyles({
    thumbnail: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        width: '100%',
        gap: 4,
        marginBottom: 10,
        '& > div': {
            overflow: 'hidden',
            aspectRatio: '200 / 185',
            width: '100%',
        }
    },
    wishlists: {
        display: 'grid',
        gap: 20,
        gridTemplateColumns: '1fr 1fr',
        // @ts-ignore
        [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
        },
    },
    wishlist: {
        display: 'grid',
        gap: 8,
        letterSpacing: 1.25,
        textTransform: 'uppercase',
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 40,
    },
    wishlistAnchor: {
        textDecoration: 'none',
        color: 'inherit',
    },
    empty: {
        height: '100%',
        width: '100%',
        background: theme.palette.primary.lightest + '90',
    },
    name: {

    },
    actions: {
        display: 'grid',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        gap: 20,
        gridTemplateColumns: '1fr',
        marginBottom: '40px',
        // @ts-ignore
        [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            '& > button': {
                gridColumn: 4,
            },
        },
    }
}, { name: 'account' });

export default function Account() {
    const classes = useStyles();
    const user = useUserStore();
    const service = user.getService();
    const { t } = useTranslation();
    const [ids, setIds] = useState<WishlistProductId[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!user.isLoggedIn) {
            return;
        }

        setLoading(true);

        service.getWishlists().then(r => null);
        service
            .getWishlistProductIds()
            .then(result => {
                if (Array.isArray(result)) {
                    setIds(result);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [user.isLoggedIn]);

    function renderThumbnail(wishlist: Wishlist) {
        const thumbnails = ids
            .filter(item => item.wishlist_id === wishlist.id)
            .map(item => <MyNewportImage productUuid={item.product_id} alt={'Product image'} />)
            .slice(0, 4);

        // Ensure we always return exactly 4 elements
        return [...thumbnails, ...Array(4 - thumbnails.length).fill(<div className={classes.empty} />)];
    }

    function renderWishlists() {
        if (!Array.isArray(user.wishlists)) {
            return null;
        }

        return user.wishlists.map(wishlist => {
            return (
                <Link href={`/account/wishlists/edit/${wishlist.id}`}>
                    <a className={classes.wishlistAnchor}>
                        <div className={classes.wishlist}>
                            <div className={classes.name}>{wishlist.name}</div>
                            <div className={classes.thumbnail}>{renderThumbnail(wishlist)}</div>
                        </div>
                    </a>
                </Link>
            );
        });
    }

    return (
        <NavigationLayout title={t('account:navigation-wishlists')} loading={loading}>
            <div className={classes.actions}>
                <CallToActionButton href={'#create-wishlist'}>{t('account:create-wishlist')}</CallToActionButton>
            </div>
            <If condition={user.wishlists.length}>
                <div className={classes.wishlists}>{renderWishlists()}</div>
            </If>
        </NavigationLayout>
    );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const appData = await getAppData(context.req);
    return {
        props: {
            appData,
        },
    };
};
